import { useEffect, useState } from "react";
import PageLayout from "../components/PageLayout";
import RoundBox from "../components/RoundBox";
import { buttonStyle, buttonBackgroundColor, GH_COLORS } from "../components/Style";
import { apiClient } from "../services/api";

/* ───── 타입 ───── */
interface Template {
  id: number;
  name: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

/* ───── 문법 안내 테이블 ───── */
const SYNTAX_HELP: [string, string][] = [
  ["#{담당선생님}", "담당 선생님 이름으로 자동 치환"],
  ["#{학생이름}", "학생 이름으로 자동 치환"],
  ["#{월} #{일} #{요일}", "발송 날짜로 자동 치환"],
  ["${텍스트박스}", "발송 시 텍스트 입력란으로 표시"],
  ["${Unordered List}", "발송 시 목록 입력란으로 표시 (항목마다 ― 자동 추가)"],
];

/* ───── 에디터 섹션 ───── */
function TemplateEditor({
  editingId,
  name,
  body,
  saving,
  onNameChange,
  onBodyChange,
  onSave,
  onCancel,
}: {
  editingId: number | null;
  name: string;
  body: string;
  onNameChange: (v: string) => void;
  onBodyChange: (v: string) => void;
  saving?: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const canSave = name.trim().length > 0 && !saving;
  return (
    <RoundBox label={editingId ? "템플릿 수정" : "새 템플릿 작성"} style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* 이름 */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: "#444", display: "block", marginBottom: 4 }}>
            템플릿 이름
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="예: 수업 안내 문자"
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid #d0d7de",
              borderRadius: 6,
              padding: "8px 10px",
              fontSize: 14,
              outline: "none",
              fontFamily: "inherit",
            }}
          />
        </div>

        {/* 내용 */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: "#444", display: "block", marginBottom: 4 }}>
            템플릿 내용
          </label>
          <textarea
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
            rows={14}
            placeholder={
              "<안녕하세요 수학을채우다 수학학원 강사 #{담당선생님}입니다.>\n\n" +
              "<#{월} #{일} #{요일} #{학생이름}의 수업 안내문자>\n\n" +
              "1. 과제 수행도\n${Unordered List}\n\n" +
              "2. 오늘 진도 상황\n${Unordered List}\n\n" +
              "3. 오늘 과제\n${Unordered List}\n\n" +
              "${텍스트박스}"
            }
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid #d0d7de",
              borderRadius: 6,
              padding: "8px 10px",
              fontSize: 14,
              lineHeight: 1.6,
              resize: "vertical",
              outline: "none",
              fontFamily: "inherit",
            }}
          />

          {/* 문법 안내 */}
          <div style={{
            marginTop: 8,
            padding: "10px 14px",
            backgroundColor: "#f6f8fa",
            borderRadius: 6,
            border: "1px solid #e4e8ec",
            fontSize: 12,
            color: "#555",
            lineHeight: 1.7,
          }}>
            <div style={{ fontWeight: 600, color: "#333", marginBottom: 6 }}>문법 안내</div>
            <table style={{ borderCollapse: "separate", borderSpacing: "0 1px", width: "100%" }}>
              <tbody>
                {SYNTAX_HELP.map(([token, desc]) => (
                  <tr key={token}>
                    <td style={{
                      fontFamily: "monospace",
                      color: "#0969da",
                      paddingRight: 16,
                      whiteSpace: "nowrap",
                      verticalAlign: "top",
                    }}>
                      {token}
                    </td>
                    <td style={{ color: "#555", verticalAlign: "top" }}>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 버튼 */}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onCancel} style={{ ...buttonStyle, ...buttonBackgroundColor.cancel }}>
            취소
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            style={{ ...buttonStyle, ...buttonBackgroundColor.save, opacity: canSave ? 1 : 0.5, cursor: canSave ? "pointer" : "not-allowed" }}
          >
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>
    </RoundBox>
  );
}

/* ───── 템플릿 카드 ───── */
function TemplateCard({
  template,
  onEdit,
  onDelete,
}: {
  template: Template;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div style={{
      border: `1px solid ${GH_COLORS.border}`,
      borderRadius: 8,
      backgroundColor: "#fff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      {/* 제목 */}
      <div style={{
        padding: "12px 14px",
        borderBottom: `1px solid ${GH_COLORS.border}`,
        fontWeight: 600,
        fontSize: 15,
        color: GH_COLORS.fgDefault,
        backgroundColor: "#f6f8fa",
      }}>
        {template.name}
      </div>

      {/* 내용 미리보기 */}
      <div style={{
        padding: "12px 14px",
        flex: 1,
        fontSize: 12,
        color: "#555",
        lineHeight: 1.65,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: 130,
        overflow: "hidden",
        position: "relative",
      }}>
        {template.body
          ? template.body
          : <span style={{ color: "#aaa", fontStyle: "italic" }}>내용 없음</span>
        }
        {/* 아래쪽 fade-out */}
        <div style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 28,
          background: "linear-gradient(transparent, #fff)",
          pointerEvents: "none",
        }} />
      </div>

      {/* 수정일 + 버튼 */}
      <div style={{
        padding: "8px 14px",
        borderTop: `1px solid ${GH_COLORS.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <span style={{ fontSize: 11, color: "#999" }}>
          {new Date(template.updatedAt).toLocaleDateString("ko-KR")} 수정
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            onClick={onEdit}
            style={{ ...buttonStyle, ...buttonBackgroundColor.edit, fontSize: 12, padding: "4px 10px" }}
          >
            수정
          </button>
          <button
            type="button"
            onClick={onDelete}
            style={{ ...buttonStyle, ...buttonBackgroundColor.delete, fontSize: 12, padding: "4px 10px" }}
          >
            삭제
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───── 페이지 ───── */
export default function TemplatePage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");

  const fetchTemplates = async () => {
    try {
      const data = await apiClient.getMessageTemplates();
      setTemplates(data.map((t) => ({
        id: t.id,
        name: t.templateName,
        body: t.templateContent,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
      })));
    } catch (e) {
      console.error("템플릿 목록 조회 실패", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setName("");
    setBody("");
    setShowEditor(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openEdit = (t: Template) => {
    setEditingId(t.id);
    setName(t.name);
    setBody(t.body);
    setShowEditor(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancel = () => {
    setShowEditor(false);
    setEditingId(null);
    setName("");
    setBody("");
  };

  const save = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      if (editingId !== null) {
        await apiClient.updateMessageTemplate(editingId, name.trim(), body);
      } else {
        await apiClient.createMessageTemplate(name.trim(), body);
      }
      await fetchTemplates();
      cancel();
    } catch (e) {
      console.error("템플릿 저장 실패", e);
      alert("저장에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id: number) => {
    if (!window.confirm("이 템플릿을 삭제하시겠습니까?")) return;
    try {
      await apiClient.deleteMessageTemplate(id);
      await fetchTemplates();
    } catch (e) {
      console.error("템플릿 삭제 실패", e);
      alert("삭제에 실패했습니다. 다시 시도해주세요.");
    }
  };

  return (
    <PageLayout
      title="템플릿 관리"
      actionRows={[
        <button
          type="button"
          onClick={openCreate}
          style={{ ...buttonStyle, ...buttonBackgroundColor.save }}
        >
          + 템플릿 작성
        </button>,
      ]}
    >
      {showEditor && (
        <TemplateEditor
          editingId={editingId}
          name={name}
          body={body}
          saving={saving}
          onNameChange={setName}
          onBodyChange={setBody}
          onSave={save}
          onCancel={cancel}
        />
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#888", fontSize: 14 }}>
          불러오는 중...
        </div>
      ) : templates.length === 0 ? (
        <div style={{
          textAlign: "center",
          padding: "60px 0",
          color: "#888",
          fontSize: 14,
        }}>
          등록된 템플릿이 없습니다. '템플릿 작성' 버튼으로 추가해 보세요.
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 16,
        }}>
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              onEdit={() => openEdit(t)}
              onDelete={() => del(t.id)}
            />
          ))}
        </div>
      )}
    </PageLayout>
  );
}
